// The boot sequence on the Clockers 64: from the film's "READY." to the calendar program.
//
//   READY.
//   LOAD"KALENDER",8,1        typed, at a human pace
//
//   SEARCHING FOR KALENDER
//   LOADING
//   READY.
//   RUN                       typed
//   -> the screen clears into 40 columns and the program draws itself line by line
//
// A key press or click fast-forwards to the end. The accessible log gets whole lines, not keys.

import type { Terminal } from "./terminal";

export interface BootHooks {
  /** a whole line appeared (for the accessible log) */
  line(text: string): void;
  /** the screen is about to clear into the program (for a CRT flash) */
  clearing(): void;
  /** draw the program's first screen into the terminal, line by line via `step` */
  program(term: Terminal, step: (ms: number) => Promise<void>): Promise<void>;
}

export class Boot {
  private fast = false;
  private paused = false;
  private wake: (() => void) | null = null;
  done = false;

  constructor(private term: Terminal, private hooks: BootHooks) {}

  /** Fast-forward: every remaining pause becomes zero. */
  skip(): void {
    this.fast = true;
    this.wake?.();
  }

  /** Freeze the boot's clock (the device notice is up); `resume` continues where it was. */
  pause(): void {
    this.paused = true;
    this.wake?.(); // let the sleeper book the active time up to this moment
  }

  resume(): void {
    this.paused = false;
    this.wake?.();
  }

  /** A pause that only counts time while the boot is not paused, and ends at once on skip. */
  private async sleep(ms: number): Promise<void> {
    let left = ms;
    while (!this.fast && (left > 0 || this.paused)) {
      const active = !this.paused; // only time spent unpaused counts
      const step = active ? Math.min(left, 100) : 1e9;
      const t0 = performance.now();
      await new Promise<void>((r) => {
        const id = window.setTimeout(() => {
          this.wake = null;
          r();
        }, step);
        this.wake = () => {
          window.clearTimeout(id);
          this.wake = null;
          r();
        };
      });
      if (active) left -= performance.now() - t0;
    }
    // even when fast-forwarding, never write to the screen while it is paused
    while (this.paused) await new Promise<void>((r) => (this.wake = () => ((this.wake = null), r())));
  }

  private async typeLine(text: string): Promise<void> {
    for (const ch of text) {
      this.term.type(ch);
      // a person typing: 70-150 ms per key, a little longer after quotes and commas
      const extra = ch === '"' || ch === "," ? 90 : 0;
      await this.sleep(70 + Math.random() * 80 + extra);
    }
  }

  async run(): Promise<void> {
    const t = this.term;
    t.cursor.on = true;
    await this.sleep(1100);
    await this.typeLine('LOAD"KALENDER",8,1');
    await this.sleep(260);
    t.newline("");
    this.hooks.line('LOAD"KALENDER",8,1');
    t.cursor.on = false;
    await this.sleep(240);
    t.basicLines[t.basicLines.length - 1] = "SEARCHING FOR KALENDER";
    t.dirty = true;
    this.hooks.line("SEARCHING FOR KALENDER");
    await this.sleep(900);
    t.newline("LOADING");
    t.basicLines.pop();
    t.dirty = true;
    this.hooks.line("LOADING");
    await this.sleep(1500);
    t.newline("READY.");
    this.hooks.line("READY.");
    t.cursor.on = true;
    await this.sleep(650);
    await this.typeLine("RUN");
    await this.sleep(300);
    t.cursor.on = false;
    this.hooks.line("RUN");
    this.hooks.clearing();
    await this.sleep(180);
    t.gridMode();
    await this.hooks.program(t, (ms) => this.sleep(ms));
    this.done = true;
  }
}
